/**
 * Placeholder contract (client copy).
 *
 * A Placeholder is a `{name}` token the runtime fills by interpolation. A saved
 * Prompt Version is served to every Organization, so dropping a known one or
 * inventing an unknown one breaks interpolation for all customers. The Save
 * guard uses this to soft-block (overridable) before sending. Mirrors the
 * server module in services/server/src/services/promptEdit/placeholders.ts.
 */

export interface PlaceholderComparison {
  removed: string[];
  added: string[];
}

export function extractPlaceholders(template: string): string[] {
  // Match every `{identifier}` directly on the raw template. We deliberately do
  // NOT treat `{{`/`}}` as escapes: the runtime does a plain single-brace
  // replace, and stripping doubled braces is order-dependent — it silently loses
  // real placeholders adjacent to a literal brace (`{summary}}`) and flips on
  // odd/even brace runs. Over-counting an f-string escape is a harmless,
  // overridable soft-block; losing a real placeholder is the dangerous direction.
  // Keep in sync with services/server/src/services/promptEdit/placeholders.ts.
  const matches = template.match(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g) ?? [];
  const names = matches.map((m) => m.slice(1, -1));
  return [...new Set(names)].sort();
}

export function comparePlaceholders(base: string, draft: string): PlaceholderComparison {
  const baseSet = new Set(extractPlaceholders(base));
  const draftSet = new Set(extractPlaceholders(draft));
  return {
    removed: [...baseSet].filter((p) => !draftSet.has(p)).sort(),
    added: [...draftSet].filter((p) => !baseSet.has(p)).sort(),
  };
}

export interface PlaceholderIntegrity extends PlaceholderComparison {
  ok: boolean;
}

export function checkPlaceholderIntegrity(base: string, draft: string): PlaceholderIntegrity {
  const { removed, added } = comparePlaceholders(base, draft);
  return { ok: removed.length === 0 && added.length === 0, removed, added };
}

/** Human-readable soft-block warnings for a non-ok verdict. */
export function describePlaceholderViolations(verdict: PlaceholderIntegrity): string[] {
  const messages: string[] = [];
  for (const name of verdict.removed) {
    messages.push(`{${name}} was removed — this will break runtime interpolation.`);
  }
  for (const name of verdict.added) {
    messages.push(`{${name}} is a new, unknown placeholder — the runtime can't fill it.`);
  }
  return messages;
}
